import React from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import type { ListProps } from "@mui/material/List";
import type { DividerProps } from "@mui/material/Divider";
import type { BaseMoleculeProps } from "@/components/types";

export interface ItemsWithDividerProps extends BaseMoleculeProps {
  /**
   * Array of items to render
   */
  items: React.ReactNode[];

  /**
   * Custom render function for each item
   */
  renderItem?: (item: React.ReactNode, index: number) => React.ReactNode;

  /**
   * Whether to show dividers between items
   */
  showDividers?: boolean;

  /**
   * Whether to show divider after last item
   */
  showLastDivider?: boolean;

  /**
   * Custom divider component or props
   */
  dividerProps?: DividerProps;

  /**
   * Spacing between items (when not using dividers)
   */
  spacing?: number;

  /**
   * List orientation
   */
  orientation?: "vertical" | "horizontal";

  /**
   * Additional List props
   */
  listProps?: Omit<ListProps, "children">;
}

const ItemsWithDividerComponent: React.FC<ItemsWithDividerProps> = ({
  items,
  renderItem,
  showDividers = true,
  showLastDivider = false,
  dividerProps,
  spacing = 0,
  orientation = "vertical",
  variant = "default",
  className,
  "data-testid": dataTestId,
  listProps,
  children,
}) => {
  // If no items, render children or nothing
  if (!items || items.length === 0) {
    return <>{children}</>;
  }

  const isHorizontal = orientation === "horizontal";

  return (
    <Box
      className={className}
      data-testid={dataTestId}
      sx={{
        // Variant-specific styling
        ...(variant === "compact" && {
          "& .MuiListItem-root": {
            paddingTop: 0.5,
            paddingBottom: 0.5,
          },
        }),
        ...(variant === "expanded" && {
          "& .MuiListItem-root": {
            paddingTop: 2,
            paddingBottom: 2,
          },
        }),
      }}
    >
      <List
        sx={{
          padding: 0,
          // Horizontal layout support
          ...(isHorizontal && {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            "& .MuiListItem-root": {
              width: "auto",
              paddingLeft: 0,
              paddingRight: spacing || 1,
            },
          }),
          // Mobile-first responsive styling
          "& .MuiListItem-root": {
            paddingLeft: {
              xs: 1, // Mobile
              md: 2, // Desktop
            },
            paddingRight: {
              xs: 1, // Mobile
              md: 2, // Desktop
            },
          },
        }}
        {...listProps}
      >
        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;
          const shouldShowDivider =
            showDividers && (!isLastItem || showLastDivider);

          return (
            <React.Fragment key={index}>
              <ListItem
                sx={{
                  // Remove default padding for horizontal layout
                  ...(isHorizontal && {
                    paddingTop: 0,
                    paddingBottom: 0,
                  }),
                  // Spacing for non-divider layouts
                  ...(!showDividers &&
                    !isHorizontal && {
                      marginBottom: spacing,
                    }),
                }}
              >
                {renderItem ? renderItem(item, index) : item}
              </ListItem>

              {shouldShowDivider && (
                <Divider
                  orientation={isHorizontal ? "vertical" : "horizontal"}
                  flexItem={isHorizontal}
                  sx={{
                    // Mobile-first responsive divider styling
                    ...(isHorizontal
                      ? {
                          height: "auto",
                          marginLeft: 1,
                          marginRight: 1,
                        }
                      : {
                          marginLeft: {
                            xs: 1, // Mobile
                            md: 2, // Desktop
                          },
                          marginRight: {
                            xs: 1, // Mobile
                            md: 2, // Desktop
                          },
                        }),
                    // Variant-specific divider styling
                    ...(variant === "compact" && {
                      marginTop: 0.5,
                      marginBottom: 0.5,
                    }),
                    ...(variant === "expanded" && {
                      marginTop: 2,
                      marginBottom: 2,
                    }),
                  }}
                  {...dividerProps}
                />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

// Apply memoization with React.memo
export const ItemsWithDivider = React.memo(ItemsWithDividerComponent);

// Set display name for debugging
(ItemsWithDivider as any).displayName = "ItemsWithDivider";
